/// <reference types="vite/client" />

/*
 * Client DynamoDB e React hooks per operazioni CRUD e query su tabelle DynamoDB.
 * Fornisce hooks per get, put, delete, query e scan, con invalidazione automatica della cache React Query.
 */

import { DynamoDBClient } from "@aws-sdk/client-dynamodb";
import {
  DynamoDBDocument,
  type GetCommandInput,
  type QueryCommandInput,
  type ScanCommandInput,
} from "@aws-sdk/lib-dynamodb";
import { type NativeAttributeValue } from "@aws-sdk/util-dynamodb";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type MutationOptions,
  type UseQueryOptions,
} from "@tanstack/react-query";
import { useEffect } from "react";
import type { Optional } from "utility-types";
import { credentialProvider, region } from "./aws";
const client = new DynamoDBClient({
  region,
  credentials: credentialProvider,
});
const ddbDocClient = DynamoDBDocument.from(client, {
  marshallOptions: {},
  unmarshallOptions: {},
});

// Nome della tabella DynamoDB per la presenza
export const PresenceTableName = import.meta.env
  .VITE_PRESENCE_TABLE_NAME as string;
// Esporta il client per uso diretto quando necessario
export { ddbDocClient };

/**
 * Hook React Query per ottenere un singolo item da DynamoDB tramite chiave primaria.
 * @template T Tipo dell'item restituito
 * @param {GetCommandInput} command - Comando DynamoDB get
 * @param {Omit<UseQueryOptions<T | null>, "queryFn" | "queryKey">} options - Opzioni React Query
 * @returns {UseQueryResult<T | null>} Risultato della query
 */
export function useDynamoGetItem<T>(
  command: GetCommandInput,
  options: Omit<UseQueryOptions<T | null>, "queryFn" | "queryKey">,
) {
  return useQuery({
    queryKey: [
      "dynamo",
      command.TableName,
      "item",
      command.Key!.pk,
      command.Key!.sk,
      command,
    ],
    ...options,
    queryFn: async () => {
      const res = await ddbDocClient.get(command);
      return (res.Item as T) || null;
    },
  });
}

// Tipo oggetto generico per item DynamoDB
type NeutralObject = Record<string, NativeAttributeValue>;

/**
 * Hook React Query per inserire o aggiornare un item in DynamoDB.
 * Aggiorna il campo lastUpdate e invalida la cache delle query correlate.
 * @template T Tipo dell'item
 * @param {string} tableName - Nome della tabella
 * @param {Omit<MutationOptions<T, Error, T>, "mutationFn">} options - Opzioni React Query
 * @returns {UseMutationResult<T, Error, T>} Risultato della mutazione
 */
export function useDynamoMutateItem<T extends NeutralObject = NeutralObject>(
  tableName: string,
  options: Omit<MutationOptions<T, Error, T>, "mutationFn">,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["dynamo", tableName],
    mutationFn: async (item: T) => {
      const fullItem = {
        ...item,
        lastUpdate: new Date().getTime(),
      };
      const input = {
        TableName: tableName,
        Item: fullItem,
        Expected: item.lastUpdate
          ? { lastUpdate: { Value: item.lastUpdate } }
          : undefined,
      };
      await ddbDocClient.put(input);
      return fullItem as T;
    },
    onSuccess: (data, variables, result, context) => {
      // Invalida la cache delle query correlate all'item e alla tabella
      queryClient.invalidateQueries({
        queryKey: ["dynamo", tableName, "item", variables.pk, variables.sk],
      });
      queryClient.invalidateQueries({
        queryKey: ["dynamo", tableName, "scan"],
      });
      queryClient.invalidateQueries({
        queryKey: ["dynamo", tableName, "query"],
      });
      if (options.onSuccess) {
        options.onSuccess(data, variables, result, context);
      }
    },
    ...options,
  });
}

/**
 * Hook React Query per cancellare un item da DynamoDB tramite chiave primaria.
 * Invalida la cache delle query correlate.
 * @param {string} tableName - Nome della tabella
 * @param {Omit<MutationOptions<void, Error, NeutralObject>, "mutationFn">} options - Opzioni React Query
 * @returns {UseMutationResult<void, Error, NeutralObject>} Risultato della mutazione
 */
export function useDynamoDeleteItem(
  tableName: string,
  options: Omit<MutationOptions<void, Error, NeutralObject>, "mutationFn">,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["dynamo", tableName, "delete"],
    mutationFn: async ({
      key,
      lastUpdate,
    }: {
      key: NeutralObject;
      lastUpdate?: number;
    }) => {
      await ddbDocClient.delete({
        TableName: tableName,
        Key: key,
        Expected: lastUpdate ? { lastUpdate: key.lastUpdate } : undefined,
      });
    },
    onSuccess: (data, variables, result, context) => {
      // Invalida la cache delle query correlate all'item e alla tabella
      queryClient.invalidateQueries({
        queryKey: [
          "dynamo",
          tableName,
          "item",
          variables.key.pk,
          variables.key.sk,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: ["dynamo", tableName, "scan"],
      });
      queryClient.invalidateQueries({
        queryKey: ["dynamo", tableName, "query"],
      });
      if (options.onSuccess) {
        options.onSuccess(data, variables, result, context);
      }
    },
    ...options,
  });
}

/**
 * Hook React Query per eseguire una query DynamoDB (con KeyConditionExpression).
 * Supporta paginazione automatica (fullQuery) e reset opzionale su disabilitazione.
 * @template T Tipo degli item restituiti
 * @param {QueryCommandInput} command - Comando DynamoDB query
 * @param {UseQueryOptions} options - Opzioni React Query
 * @param {boolean} fullQuery - Se true, recupera tutte le pagine
 * @param {boolean} resetOnDisabled - Se true, resetta la cache quando la query è disabilitata
 * @returns {UseQueryResult<T[]>} Risultato della query
 */
export function useDynamoQuery<T>(
  command: QueryCommandInput,
  options: Optional<Omit<UseQueryOptions<T[]>, "queryFn">, "queryKey"> = {},
  fullQuery = true,
  resetOnDisabled = false,
) {
  const queryClient = useQueryClient();
  const compiledOptions = {
    queryKey: ["dynamo", command.TableName, "query", command],
    ...options,
    queryFn: async () => {
      let res = await ddbDocClient.query(command);
      const data = (res.Items || []) as T[];
      while (fullQuery && res.LastEvaluatedKey) {
        res = await ddbDocClient.query({
          ...command,
          ExclusiveStartKey: res.LastEvaluatedKey,
        });
        data.push(...((res.Items || []) as T[]));
      }
      return data;
    },
  } as const;
  const res = useQuery(compiledOptions);

  // Reset della cache opzionale quando la query viene disabilitata
  useEffect(() => {
    if (resetOnDisabled && options.enabled === false) {
      queryClient.resetQueries({
        queryKey: compiledOptions.queryKey,
      });
    }
  }, [options.enabled, resetOnDisabled, queryClient, compiledOptions.queryKey]);
  return res;
}

/**
 * Hook React Query per eseguire una scansione DynamoDB (ScanCommand).
 * Supporta paginazione automatica (fullQuery) e reset opzionale su disabilitazione.
 * @template T Tipo degli item restituiti
 * @param {ScanCommandInput} command - Comando DynamoDB scan
 * @param {UseQueryOptions} options - Opzioni React Query
 * @param {boolean} fullQuery - Se true, recupera tutte le pagine
 * @param {boolean} resetOnDisabled - Se true, resetta la cache quando la query è disabilitata
 * @returns {UseQueryResult<T[]>} Risultato della scansione
 */
export function useDynamoScan<T>(
  command: ScanCommandInput,
  options: Optional<Omit<UseQueryOptions<T[]>, "queryFn">, "queryKey"> = {},
  fullQuery = true,
  resetOnDisabled = false,
) {
  const queryClient = useQueryClient();
  const compiledOptions = {
    queryKey: ["dynamo", command.TableName, "scan", command],
    ...options,
    queryFn: async () => {
      let res = await ddbDocClient.scan(command);
      const data = (res.Items || []) as T[];
      while (fullQuery && res.LastEvaluatedKey) {
        res = await ddbDocClient.scan({
          ...command,
          ExclusiveStartKey: res.LastEvaluatedKey,
        });
        data.push(...((res.Items || []) as T[]));
      }
      return data;
    },
  } as const;
  const res = useQuery(compiledOptions);
  useEffect(() => {
    if (resetOnDisabled && options.enabled === false) {
      queryClient.resetQueries({
        queryKey: compiledOptions.queryKey,
      });
    }
  }, [options.enabled, resetOnDisabled, queryClient, compiledOptions.queryKey]);
  return res;
}
